<template>
  <view-section
    :title="t('releases')"
    :panel-loading="panelLoading"
    :padding-y="false"
    :padding-x="false"
    v-bind="$attrs"
    :expandable="expandable"
  >
    <loading-content v-if="panelLoading || (!modfile && loading)" />
    <div v-else-if="modfile">
      <button
        class="tw-group tw-w-full tw-text-left tw-space-x-1"
        :class="Classes.RELEASES_LINK"
        @click="showReleaseModal"
      >
        <div
          :class="Classes.RELEASES_CONTENT"
          class="game-tile-background-color-hover tw-p-4"
        >
          <div
            class="tw-flex tw-justify-between tw-space-x-2 tw-items-center tw-w-full"
          >
            <div class="tw-w-9/12">
              <span
                class="tw-truncate tw-w-full tw-block tw-text-h6 tw-font-bold"
              >
                {{ t('version') }}
                {{ modfile.version || 'N/A' }}
              </span>
              <div class="tw-space-x-2 tw-flex">
                <tooltip-hover :text="t('filesize')">
                  <div class="tw-flex tw-items-center tw-text-sm">
                    <font-awesome-icon icon="database" class="tw-mr-1" />
                    <span>{{ filesize }}</span>
                  </div>
                </tooltip-hover>
                <div class="tw-flex tw-items-center tw-text-sm">
                  <font-awesome-icon icon="cloud-upload-alt" class="tw-mr-1" />
                  <span>
                    <base-time-tooltip
                      :prefix="t('added')"
                      :timestamp="modfile.date_added"
                    >
                      {{ timeSince(modfile.date_added) }}
                      {{ t('ago') }}
                    </base-time-tooltip>
                  </span>
                </div>
              </div>
            </div>

            <tooltip-hover
              v-if="showDownloadButton && modfile.download?.binary_url"
              :text="tooltip || t('download')"
            >
              <base-button
                v-if="authRequired || paidRequired || !isDownloadable"
                small
                secondary
                no-tab
                :sr-only="t('download')"
                icon="download"
                :disabled="!isDownloadable"
                :progress="progress"
                :status="downloadingId === modfile.id ? status : null"
                @click.stop="handleAuthDownload(modfile)"
              />
              <a
                v-else
                target="_blank"
                class="mod-input-btn--small game-secondary-button-color game-secondary-text hover:game-secondary-button-color-hover focus:game-secondary-button-color-hover"
                :href="modfile.download.binary_url"
                :disabled="!isDownloadable"
                @click.stop
              >
                <font-awesome-icon icon="download" />
              </a>
            </tooltip-hover>
          </div>
          <p
            v-if="modfile.changelog"
            class="tw-max-h-20 tw-mt-4 tw-text-sm tw-line-clamp-3"
          >
            {{ modfile.changelog }}
          </p>
        </div>
      </button>
      <div
        :class="Classes.FILE_DIVIDER"
        class="tw-border-t tw-border-[var(--tile-text)] tw-opacity-30"
      />

      <base-button
        :class="Classes.RELEASES_PREVIOUS_BUTTON"
        class="tw-ml-1 tw-my-2 tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100"
        small
        text-link-hover
        @click="getOtherReleases"
      >
        {{ t('view_previous_releases') }}
      </base-button>
    </div>

    <div v-else class="tw-opacity-80 tw-m-3">
      {{ t('no_files_published') }}.
    </div>
  </view-section>

  <teleport v-if="modfile" to="body">
    <base-modal
      has-close-btn
      :heading="t('previous_releases')"
      :show="isModalOpen"
      @close:modal="hideModal"
    >
      <modio-table
        v-if="loading || error || items?.data?.length"
        :table-id="TABLE_ID"
        :fetch="false"
        :loading="loading"
        :error="error"
        :limit="10"
        :row-schema="computedSchema"
        :items="items"
        :item-total="items?.data?.length"
      />
      <p v-else>{{ t('no_previous_releases') }}.</p>
    </base-modal>

    <base-modal
      has-close-btn
      :cancel-button="false"
      size="md"
      :show="isReleaseModalOpen"
      :heading="t('release_notes')"
      @close:modal="hideReleaseModal"
    >
      <ul>
        <li>
          <div class="tw-flex tw-justify-between">
            <span class="tw-opacity-70">{{ t('name') }}</span>
            <span>{{ modfile.filename }}</span>
          </div>
          <div
            class="tw-border-t tw-border-[var(--modal-text-color)] tw-opacity-20 tw-my-2"
          />
        </li>
        <li v-if="modfile.version">
          <div class="tw-flex tw-justify-between">
            <span class="tw-opacity-70">{{ t('version') }}</span>
            <span v-decode="modfile.version" />
          </div>
          <div
            class="tw-border-t tw-border-[var(--modal-text-color)] tw-opacity-20 tw-my-2"
          />
        </li>
        <li>
          <div class="tw-flex tw-justify-between">
            <span class="tw-opacity-70">{{ t('filesize') }}</span>
            <span
              >{{ humanNumberFormat(modfile.filesize) }} {{ t('bytes') }} ({{
                filesize
              }})</span
            >
          </div>
          <div
            class="tw-border-t tw-border-[var(--modal-text-color))] tw-opacity-20 tw-my-2"
          />
        </li>
        <li>
          <div class="tw-flex tw-justify-between">
            <span class="tw-opacity-70">{{ t('added') }} </span>
            <span>
              <base-time-tooltip :timestamp="modfile.date_added">{{
                humanDateFormat(modfile.date_added)
              }}</base-time-tooltip>
            </span>
          </div>
          <div
            class="tw-border-t tw-border-[var(--modal-text-color))] tw-opacity-20 tw-my-2"
          />
        </li>
        <li v-if="modfile.platforms.length">
          <div class="tw-flex tw-justify-between">
            <span class="tw-opacity-70">{{ t('platforms', 2) }}</span>
            <span>{{ formatPlatformText(modfile.platforms) }}</span>
          </div>
          <div
            class="tw-border-t tw-border-[var(--modal-text-color))] tw-opacity-20 tw-my-2"
          />
        </li>
      </ul>

      <div class="tw-space-y-4 tw-mt-4">
        <p
          v-if="modfile.changelog"
          v-decode="modfile.changelog"
          class="tw-whitespace-pre-line tw-opacity-80 tw-text-sm"
        />
        <p v-else class="tw-opacity-80">{{ t('no_release_notes') }}</p>
      </div>
      <template
        v-if="showDownloadButton && modfile.download?.binary_url"
        #footer
      >
        <tooltip-hover fixed :text="tooltip">
          <base-button
            v-if="authRequired || paidRequired || !isDownloadable"
            primary
            :progress="progress"
            :disabled="!isDownloadable"
            :status="downloadingId === modfile.id ? status : null"
            @click.stop="handleAuthDownload(modfile)"
          >
            {{ t('download') }}
          </base-button>
          <a
            v-else
            target="_blank"
            class="mod-input-btn--base tw-w-36 mod-input--height game-primary-button-color hover:game-primary-button-color-hover focus:game-primary-button-color-hover game-primary-text tw-text-sm"
            :href="modfile.download.binary_url"
            :disabled="!isDownloadable"
            @click.stop
          >
            {{ t('download') }}
          </a>
        </tooltip-hover>
      </template>
    </base-modal>
  </teleport>
</template>

<script>
import LoadingContent from '@components/Loading/LoadingContent.vue'
import { downloadFileRequest, getAllFilesRequest } from '@services'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { computed, provide, toRefs, watch, ref } from 'vue'
import ViewSection from '@components/View/ViewSection.vue'
import ModioTable from '@components/Table/ModioTable.vue'
import { schema } from '@config/schemas/modFiles.js'
import { purchasesStore, authStore } from '@stores'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { saveAs } from 'file-saver'
import { useI18n } from 'vue-i18n'
import {
  isMarketplaceEnabledForMod,
  restrictDownloadsAuth,
  restrictDownloadsPaid,
  allowDirectDownloads,
} from '@helpers/bitHelper.js'
import {
  humanNumberFormat,
  humanDateFormat,
  humanFilesize,
  isObjEmpty,
  timeSince,
  clone,
} from '@helpers/utils.js'
import {
  useResourceValue,
  usePermissions,
  useStatus,
  useTable,
  useAsync,
  useModal,
} from '@composables'
import {
  PLATFORM_STATUS_APPROVED,
  GAME_PLATFORM_LABELS,
  PLATFORM_SOURCE,
} from '@config/options.js'

const TABLE_ID = 'mod-files-table'

export default {
  components: {
    LoadingContent,
    TooltipHover,
    ViewSection,
    ModioTable,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    mod: {
      type: Object,
      default: null,
    },
    panelLoading: {
      type: Boolean,
      default: false,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { game, mod } = toRefs(props)
    const downloadingId = ref(null)
    const modfile = ref(null)
    const progress = ref(0)

    const { modOwned } = purchasesStore()
    const { isLoggedIn } = authStore()

    const { isModalOpen, showModal, hideModal } = useModal()
    const { hasPermissionForMod } = usePermissions()
    const { defaultSortQuery } = useTable()
    const { ugcName } = useResourceValue()
    const { t } = useI18n()

    const {
      isModalOpen: isReleaseModalOpen,
      showModal: showReleaseModal,
      hideModal: hideReleaseModal,
    } = useModal()

    const { data, loading, error, run } = useAsync(() =>
      getAllFilesRequest(
        mod.value.game_name_id,
        mod.value.name_id,
        defaultSortQuery(schema)
      )
    )

    const {
      loading: loadingFileDownload,
      error: errorFileDownload,
      data: dataFileDownload,
      run: runFileDownload,
    } = useAsync(
      (binary_url) =>
        downloadFileRequest(binary_url, (ev) => {
          progress.value = ev.loaded / ev.total
        }),
      t('download_file_error')
    )

    const { status, updateStatus, statusType } = useStatus({
      loading: loadingFileDownload,
      error: errorFileDownload,
    })
    const statusObj = computed(() => ({
      status: status.value,
      actionName: 'handleAuthDownload',
      id: downloadingId.value,
    }))

    const showDownloadButton = computed(() => Settings.value.allowDownloading)

    const filesize = computed(
      () => modfile.value && humanFilesize(modfile.value.filesize)
    )

    const computedSchema = computed(() => {
      const _schema = clone(schema)
      if (!showDownloadButton.value) {
        _schema.config = _schema.config.filter((i) => i.label !== 'download')
      }
      return _schema
    })

    const canDirectDownload = allowDirectDownloads(
      game.value.api_access_options
    )

    const authRequired = restrictDownloadsAuth(game.value.api_access_options)

    const paidRequired = computed(
      () =>
        restrictDownloadsPaid(game.value.api_access_options) && mod.value?.price
    )

    const restrictDownloadAuth = computed(
      () => authRequired && !isLoggedIn.value
    )

    const restrictDownloadNotPurchased = computed(
      () =>
        paidRequired.value &&
        isMarketplaceEnabledForMod(mod.value?.monetization_options) &&
        !modOwned(mod.value?.id) &&
        !hasPermissionForMod(
          mod.value?.name_id,
          game.value.name_id,
          'moderator'
        )
    )

    const isDownloadable = computed(
      () =>
        canDirectDownload &&
        !restrictDownloadAuth.value &&
        !restrictDownloadNotPurchased.value
    )

    const items = computed(() =>
      data.value?.data
        ? {
            data: data.value.data
              .filter((file) => file.id !== modfile.value.id && !isSource(file))
              .map((file) => ({
                ...file,
                authRequired,
                paidRequired: paidRequired.value,
                downloadable: isDownloadable.value,
                downloadInGame: !canDirectDownload,
                restrictDownloadAuth: restrictDownloadAuth.value,
                restrictDownloadNotPurchased:
                  restrictDownloadNotPurchased.value,
              })),
          }
        : {}
    )

    const tooltip = computed(() => {
      if (!canDirectDownload)
        return t('download_from_game_client', {
          mod: ugcName(game.value, true, true).value,
        })
      if (restrictDownloadNotPurchased.value)
        return t('download_payment_required', {
          ugc: ugcName(game.value, true, true).value,
        })
      if (restrictDownloadAuth.value)
        return t('download_auth_required', {
          ugc: ugcName(game.value, true, true).value,
        })
      return ''
    })

    watch(
      mod,
      async (_mod) => {
        if (!_mod) return

        if (_mod.modfile && !isObjEmpty(_mod.modfile)) {
          modfile.value = _mod.modfile
        } else if (_mod.platforms?.length) {
          if (!data.value) {
            await run()

            if (!error.value) {
              const liveIds = _mod.platforms.map((p) => p.modfile_live)
              // Just grab the first live file
              modfile.value = data.value.data.find((f) =>
                liveIds.includes(f.id)
              )
            }
          }
        }
      },
      { immediate: true }
    )

    async function handleAuthDownload(file) {
      if (!isDownloadable.value) return

      downloadingId.value = file.id

      await runFileDownload(file.download.binary_url)

      if (!errorFileDownload.value) {
        saveAs(dataFileDownload.value, file.filename)
        updateStatus(statusType.SUCCESS)
      }
    }

    function getOtherReleases() {
      if (!data.value) run()
      showModal()
    }

    function isSource(file) {
      return (
        file.platforms.find((p) => p.platform === PLATFORM_SOURCE) &&
        file.platforms.length === 1
      )
    }

    function formatPlatformText(platforms) {
      return platforms
        .filter(
          (platformObj) =>
            platformObj.status === PLATFORM_STATUS_APPROVED &&
            mod.value?.platforms.some(
              (p) =>
                p.platform === platformObj.platform &&
                p.modfile_live === modfile.value.id
            )
        )
        .map((platformObj) => GAME_PLATFORM_LABELS[platformObj.platform])
        .join(', ')
    }

    provide('handleAuthDownload', handleAuthDownload)
    provide('progress', progress)
    provide('status', statusObj)

    return {
      formatPlatformText,
      isReleaseModalOpen,
      handleAuthDownload,
      showDownloadButton,
      humanNumberFormat,
      showReleaseModal,
      getOtherReleases,
      hideReleaseModal,
      humanDateFormat,
      isDownloadable,
      computedSchema,
      downloadingId,
      authRequired,
      paidRequired,
      isModalOpen,
      timeSince,
      hideModal,
      progress,
      filesize,
      TABLE_ID,
      tooltip,
      modfile,
      loading,
      Classes,
      status,
      error,
      items,
      t,
    }
  },
}
</script>
